import { ContainerReceipt } from './delivery-validation.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PurchaseShipment, ShipmentLine, ShipmentOrderSummary } from '../_interfaces/purchase-shipment';

export interface ShipmentQuery {
  pageIndex: number;
  pageSize: number;
  search?: string;
  status?: string;
  lateOnly?: boolean;
}

export interface ShipmentSummary {
  inTransit: number;
  late: number;
  arrivingSoon: number;
  awaitingClearance: number;
}

export interface ShipmentQueryResult {
  items: PurchaseShipment[];
  count: number;
  // Shipments per status with every filter except status applied (for the tabs).
  statusCounts: Record<string, number>;
  summary: ShipmentSummary;
}

export interface ShipmentInput {
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  containerNo?: string | null;
  billOfLading?: string | null;
  etd?: string | null;
  eta?: string | null;
  portOfLoading?: string | null;
  portOfDischarge?: string | null;
  vesselDetails?: string | null;
  shippingLine?: string | null;
  store: string;
  status: string;
  notes?: string | null;
  containers?: { id?: number | null; key: string; containerNo: string | null; containerType: string | null; sealNo: string | null; status: string | null; clearedAt: string | null }[];
  items: ShipmentLine[];
}

// One row of the Containers view: a line in a container, with its
// shipment's and container's details.
export interface ContainerListItem {
  id: number;
  containerId: number | null;
  containerNo: string | null;
  clearedAt: string | null;
  status: string;
  purchaseOrderId: number;
  poReference: string | null;
  productId: number | null;
  productName: string;
  sku: string | null;
  incoterm: string | null;
  quantity: number;
  unitOfMeasure: string;
  currency: string;
  unitPrice: number;
  subtotal: number;
  shipmentId: number;
  shipmentReference: string;
  invoiceDate: string | null;
  invoiceNumber: string | null;
  billOfLading: string | null;
  etd: string | null;
  eta: string | null;
  portOfDischarge: string | null;
  portOfLoading: string | null;
  vesselDetails: string | null;
  shippingLine: string | null;
  store: string;
}

// What a Containers-view import would do (or did), change by change.
export interface ContainerLinesImportResult {
  applied: boolean;
  errors: string[];
  warnings: string[];
  changes: { level: 'line' | 'container' | 'shipment'; ref: string; field: string; from: string; to: string }[];
  lines: number;
  containers: number;
  shipments: number;
}

// What a spreadsheet import would do (or did) to one shipment.
export interface ShipmentImportResult {
  key: string;
  reference: string | null;
  action: 'create' | 'update';
  containers: number;
  lines: number;
  warnings: string[];
  errors: string[];
  savedReference?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PurchaseShipmentService {
  constructor(private http: HttpClient) {}

  getShipments(query: ShipmentQuery): Observable<ShipmentQueryResult> {
    return this.http.post<ShipmentQueryResult>('/purchase-shipments/list', query);
  }

  getShipmentById(id: number): Observable<PurchaseShipment> {
    return this.http.get<PurchaseShipment>(`/purchase-shipments/${id}`);
  }

  getAvailableOrders(): Observable<ShipmentOrderSummary[]> {
    return this.http.get<ShipmentOrderSummary[]>('/purchase-shipments/available-orders');
  }

  // A purchase order's outstanding lines, ready to drop onto a shipment.
  getOrderLines(purchaseOrderId: number): Observable<ShipmentLine[]> {
    return this.http.get<ShipmentLine[]>(`/purchase-shipments/order/${purchaseOrderId}/lines`);
  }

  // Both answer with just the id (and new reference) - the form goes back to the list.
  createShipment(input: ShipmentInput): Observable<{ id: number; reference: string }> {
    return this.http.post<{ id: number; reference: string }>('/purchase-shipments', input);
  }

  updateShipment(id: number, input: ShipmentInput): Observable<{ id: number }> {
    return this.http.put<{ id: number }>(`/purchase-shipments/${id}`, input);
  }

  // Every shipment matching the list's filters, in full, for the spreadsheet.
  // `ids` (selected rows) overrides the filters.
  exportShipments(params: { search?: string; status?: string; lateOnly?: boolean; ids?: number[] }): Observable<any[]> {
    return this.http.post<any[]>('/purchase-shipments/export', params);
  }

  // Every container (the Shipments page's Containers view).
  // `all`: every matching line (for the spreadsheet), not just a page.
  listContainers(params: { search?: string; status?: string; pageIndex: number; pageSize: number; all?: boolean }): Observable<{
    items: ContainerListItem[];
    count: number;
    statusCounts: Record<string, number>;
    totals: { currency: string; total: number }[];
    units: number;
  }> {
    return this.http.post<any>('/purchase-shipments/containers/list', params);
  }

  // dryRun: true only checks the file; false saves it (nothing is saved if anything is wrong).
  importContainerLines(request: { dryRun: boolean; rows: any[] }): Observable<ContainerLinesImportResult> {
    return this.http.post<ContainerLinesImportResult>('/purchase-shipments/containers/import', request);
  }

  // One state for these containers (each container's mic / state menu); received ones are left alone.
  setContainerStatus(ids: number[], status: string): Observable<{ updated: number; skipped: number }> {
    return this.http.post<{ updated: number; skipped: number }>('/purchase-shipments/containers/status', { ids, status });
  }

  // One state for many shipments; received containers are left alone.
  bulkSetStatus(ids: number[], status: string): Observable<{ updated: number; skipped: number }> {
    return this.http.post<{ updated: number; skipped: number }>('/purchase-shipments/bulk-status', { ids, status });
  }

  // dryRun: true only checks the file; false saves it (nothing is saved if anything is wrong).
  importShipments(request: { dryRun: boolean; shipments: any[] }): Observable<{ applied: boolean; results: ShipmentImportResult[] }> {
    return this.http.post<{ applied: boolean; results: ShipmentImportResult[] }>('/purchase-shipments/import', request);
  }

  // One container in any state, through the purchasing side's access.
  getContainer(id: number): Observable<ContainerReceipt> {
    return this.http.get<ContainerReceipt>(`/purchase-shipments/containers/${id}`);
  }

  // Super admins only (the backend answers 403 for anyone else).
  deleteShipment(id: number): Observable<{ success: boolean; reference: string }> {
    return this.http.delete<{ success: boolean; reference: string }>(`/purchase-shipments/${id}`);
  }
}
